// EditShowcase.jsx
// ─────────────────────────────────────────────────────────────────────────────
// "Where your work becomes editorial", the proof section that turns The LWD Edit
// from a claim into a tangible asset. This is your unfair advantage made
// visible: 2 most-recent published Edit covers + 1 featured article preview,
// with a single CTA out to /the-edit.
//
// The whole section flips to dark, same vocabulary as The LWD Edit's masthead,
// so it reads as a magazine spread inside the brochure (a mood shift, not a
// theme switch). The page above and below stays cream.
//
// Empty-state behaviour: if no published posts exist, the section quietly
// hides itself. Truthful, never invents content.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabaseClient.js';
import { EDIT_SHOWCASE_COPY } from '../data/inclusionContent.js';
import { imgUrl } from '../../../utils/imgUrl.js';

const GD   = 'var(--font-heading-primary)';
const FS   = 'var(--font-serif-body, Georgia, "Times New Roman", serif)';
const NU   = 'var(--font-body)';
const GOLD = '#c9a96e';

// Editorial dark, matches DARK theme tokens used in The LWD Edit's hero
const BG       = '#070604';
const SURFACE  = '#0e0c09';
const TEXT     = '#f0ebe0';
const BODY     = 'rgba(240,235,224,0.78)';
const SUBTLE   = 'rgba(240,235,224,0.42)';
const BORDER   = 'rgba(240,235,224,0.10)';

export default function EditShowcase() {
  const [posts, setPosts] = useState(null); // null=loading, []=empty, [...]=data

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await supabase
          .from('magazine_posts')
          .select('id, slug, title, standfirst, excerpt, cover_image, category_label, published_at')
          .eq('published', true)
          .order('published_at', { ascending: false, nullsFirst: false })
          .order('created_at',   { ascending: false })
          .limit(3);
        if (mounted) setPosts(data || []);
      } catch {
        if (mounted) setPosts([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Loading suppression
  if (posts === null) return null;
  // Honest empty-state: hide the section entirely until real Edit content exists
  if (posts.length === 0) return null;

  // Most recent two as covers, third as the featured article preview
  const covers  = posts.slice(0, 2);
  const feature = posts[2] || posts[0];

  return (
    <section style={{
      background:  BG,
      padding:     'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 80px)',
      color:       TEXT,
      borderTop:   `1px solid ${BORDER}`,
      borderBottom:`1px solid ${BORDER}`,
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Eyebrow + title */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(48px, 6vw, 80px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 24 }}>
            <span style={{ width: 36, height: 1, background: GOLD }} />
            <span style={{
              fontFamily: NU, fontSize: 9, fontWeight: 600,
              letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD,
            }}>
              {EDIT_SHOWCASE_COPY.eyebrow}
            </span>
            <span style={{ width: 36, height: 1, background: GOLD }} />
          </div>

          {/* Kicker line above the title, the transformation statement.
              "From listing to legacy" / "From venue to destination story"
              type framing. Sets the tone before the title lands. */}
          {EDIT_SHOWCASE_COPY.kicker && (
            <p style={{
              fontFamily: GD,
              fontSize:   'clamp(15px, 1.4vw, 18px)',
              fontStyle:  'italic',
              fontWeight: 400,
              color:      'rgba(240,235,224,0.62)',
              letterSpacing: '0.02em',
              margin:     '0 auto 14px',
              lineHeight: 1.4,
            }}>
              {EDIT_SHOWCASE_COPY.kicker}
            </p>
          )}

          <h2 style={{
            fontFamily: GD,
            fontSize:   'clamp(32px, 4.5vw, 56px)',
            fontWeight: 400,
            color:      TEXT,
            lineHeight: 1.08,
            letterSpacing: '-0.01em',
            margin:     '0 auto 18px',
            maxWidth:   780,
          }}>
            {EDIT_SHOWCASE_COPY.title}
          </h2>
          <p style={{
            fontFamily: NU, fontSize: 'clamp(14px, 1.4vw, 17px)',
            color: BODY, lineHeight: 1.7, margin: '0 auto', maxWidth: 620,
          }}>
            {EDIT_SHOWCASE_COPY.body}
          </p>
        </div>

        {/* 2 covers + 1 feature, in a 12-col grid that collapses cleanly */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: 'clamp(20px, 2.4vw, 36px)',
        }}>
          {covers.map((p, idx) => (
            <CoverCard key={p.id} post={p} index={idx} />
          ))}
          <FeatureCard post={feature} />
        </div>

        {/* CTA out to The LWD Edit */}
        <div style={{ textAlign: 'center', marginTop: 'clamp(48px, 6vw, 72px)' }}>
          <a
            href={EDIT_SHOWCASE_COPY.ctaHref}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              fontFamily: NU, fontSize: 11, fontWeight: 500,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: GOLD, textDecoration: 'none',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {EDIT_SHOWCASE_COPY.cta}
            <span aria-hidden style={{
              width: 5, height: 5, background: GOLD,
              transform: 'rotate(45deg)',
            }} />
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Cover card (compact, magazine-cover proportions 3:4) ───────────────────

function CoverCard({ post, index }) {
  const [hovered, setHovered] = useState(false);
  const img = imgUrl(post.cover_image, { width: 800, resize: 'contain' }) || '';
  const cat = post.category_label || '';
  const issueNumber = String(index + 1).padStart(2, '0');

  return (
    <a
      href={`/lwd-edit/${post.slug}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        gridColumn: 'span 4',
        display: 'block',
        textDecoration: 'none', color: 'inherit',
        position: 'relative', overflow: 'hidden',
        background: SURFACE,
        transition: 'transform 0.45s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
      }}
    >
      <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', overflow: 'hidden' }}>
        {img && (
          <img
            src={img}
            alt={post.title}
            loading="lazy"
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transform: hovered ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 1.4s cubic-bezier(0.25,0.46,0.45,0.94)',
            }}
          />
        )}
        {/* Cover wash so the issue tag + title read against the photograph */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(to top, ${BG}f0 0%, ${BG}55 35%, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        {/* Issue tag, top-left */}
        <div style={{
          position: 'absolute', top: 18, left: 18,
          fontFamily: NU, fontSize: 8, fontWeight: 600,
          letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD,
        }}>
          The LWD Edit · No. {issueNumber}
        </div>

        {/* Title, bottom-left */}
        <div style={{
          position: 'absolute', left: 18, right: 18, bottom: 20,
        }}>
          {cat && (
            <div style={{
              fontFamily: NU, fontSize: 8, fontWeight: 500,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: 'rgba(240,235,224,0.65)', marginBottom: 8,
            }}>
              {cat}
            </div>
          )}
          <div style={{
            fontFamily: GD, fontSize: 'clamp(16px, 1.6vw, 20px)',
            fontWeight: 400, color: TEXT, lineHeight: 1.2,
            letterSpacing: '-0.01em',
          }}>
            {post.title}
          </div>
        </div>
      </div>
    </a>
  );
}

// ── Feature article card (wider, with standfirst preview) ──────────────────

function FeatureCard({ post }) {
  const [hovered, setHovered] = useState(false);
  const img  = imgUrl(post.cover_image, { width: 800, resize: 'contain' }) || '';
  const cat  = post.category_label || '';
  const lede = post.standfirst || post.excerpt || '';

  return (
    <a
      href={`/lwd-edit/${post.slug}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        gridColumn: 'span 4',
        display: 'flex', flexDirection: 'column',
        textDecoration: 'none', color: 'inherit',
        background: SURFACE,
        transition: 'transform 0.45s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
      }}
    >
      <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', overflow: 'hidden' }}>
        {img && (
          <img
            src={img}
            alt={post.title}
            loading="lazy"
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transform: hovered ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 1.4s cubic-bezier(0.25,0.46,0.45,0.94)',
            }}
          />
        )}
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(to top, ${BG} 0%, ${BG}66 30%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'absolute', bottom: 22, left: 22, right: 22 }}>
          <div style={{
            fontFamily: NU, fontSize: 8, fontWeight: 600,
            letterSpacing: '0.32em', textTransform: 'uppercase',
            color: GOLD, marginBottom: 12,
          }}>
            Featured Article
          </div>
          {cat && (
            <div style={{
              fontFamily: NU, fontSize: 8, fontWeight: 500,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: 'rgba(240,235,224,0.65)', marginBottom: 6,
            }}>
              {cat}
            </div>
          )}
          <h3 style={{
            fontFamily: GD, fontSize: 'clamp(20px, 2.2vw, 28px)',
            fontWeight: 400, color: TEXT, lineHeight: 1.15,
            letterSpacing: '-0.01em', margin: 0,
          }}>
            {post.title}
          </h3>
        </div>
      </div>

      {lede && (
        <div style={{
          padding: 'clamp(20px, 2vw, 28px)',
          borderTop: `1px solid ${BORDER}`,
        }}>
          <p style={{
            fontFamily: FS, fontStyle: 'italic',
            fontSize: 'clamp(13px, 1.2vw, 15px)',
            lineHeight: 1.7,
            color: BODY,
            margin: 0,
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {lede}
          </p>
          <div style={{
            marginTop: 16,
            fontFamily: NU, fontSize: 9, fontWeight: 500,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: hovered ? GOLD : SUBTLE,
            display: 'inline-flex', alignItems: 'center', gap: 8,
            transition: 'color 0.2s',
          }}>
            Read the story
            <span aria-hidden style={{
              width: 4, height: 4, background: hovered ? GOLD : SUBTLE,
              transform: 'rotate(45deg)', transition: 'background 0.2s',
            }} />
          </div>
        </div>
      )}
    </a>
  );
}
